import { useRef, useState } from "react";

import type { Side } from "../api/types";
import { usePFAwaitOutcome, usePFPlace, usePFStatus } from "../hooks/usePredict";
import { checkPFDraft, pfOutcomeText, type PFDraft } from "../lib/predict";
import { Panel } from "./Panel";

/**
 * Форма ордера на predict.fun — проба (ADR-0040).
 *
 * Нарочно беднее формы Polymarket: только лимитный ордер, цена и количество
 * долей, без аккаунтов и когорт — у пробы один кошелёк, и мультиаккаунт
 * остаётся за Polymarket. Сеть и адрес кошелька — в подзаголовке: ордер
 * тратит настоящие USDT на BNB Chain, и откуда они — видно до нажатия.
 */
export function PredictOrderForm({ slug, className }: { slug: string; className?: string }) {
  const status = usePFStatus();
  const place = usePFPlace();
  const awaitOutcome = usePFAwaitOutcome();
  // Итог приходит позже ответа: правка формы между ними его отменяет.
  const awaiting = useRef<string | null>(null);
  const [draft, setDraft] = useState<PFDraft>({
    outcome: "Up",
    side: "BUY",
    price: "",
    size: "",
    postOnly: true,
  });
  const [sent, setSent] = useState<{ text: string; tone: "ok" | "bad" | "muted" } | null>(null);

  const st = status.data;
  const win = (st?.windows ?? []).find((w) => w.slug === slug);
  const tick = win?.tick ?? "0.01";
  const check = checkPFDraft(slug, draft, tick, st?.max_order_usdt);
  const reason = !st
    ? status.error
      ? `сервис predictfun недоступен: ${String(status.error.message)}`
      : "загрузка…"
    : !st.trading
      ? `торговля ${st.trading_note}`
      : !win?.market_id
        ? "у этого окна нет рынка на predict.fun"
        : win.trading_status !== "OPEN"
          ? `рынок не принимает ордера: ${win.trading_status}`
          : null;

  function update(patch: Partial<PFDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
    setSent(null);
    awaiting.current = null;
    place.reset();
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!check.request || reason) return;
    place.mutate(check.request, {
      onSuccess: (r) => {
        awaiting.current = r.order_id;
        setSent({ text: `принят: ${r.order_id} · ${r.notional} USDT — проверяется…`, tone: "muted" });
        awaitOutcome(r.order_id).then(
          (o) => {
            if (awaiting.current === r.order_id) setSent(pfOutcomeText(o));
          },
          (e: unknown) => {
            if (awaiting.current === r.order_id)
              setSent({ text: `итог не получен: ${e instanceof Error ? e.message : String(e)}`, tone: "bad" });
          },
        );
      },
    });
  }

  const subtitle = st
    ? `${st.network} · ${st.wallet_kind === "predict" ? "аккаунт " : ""}` +
      `${st.address ? `${st.address.slice(0, 6)}…${st.address.slice(-4)}` : "без ключа"}` +
      (st.balance_usdt ? ` · ${st.balance_usdt} USDT` : "")
    : undefined;

  return (
    <Panel title="Новый ордер · predict.fun" subtitle={subtitle} className={className} dense>
      <form className="flex flex-col gap-2" onSubmit={submit}>
        <div className="flex gap-1">
          {(["BUY", "SELL"] as Side[]).map((side) => (
            <Choice
              key={side}
              on={draft.side === side}
              tone={side === "BUY" ? "var(--color-buy)" : "var(--color-sell)"}
              onClick={() => update({ side })}
              big
            >
              {side === "BUY" ? "Купить" : "Продать"}
            </Choice>
          ))}
        </div>
        <div className="flex gap-1">
          {(["Up", "Down"] as const).map((o) => (
            <Choice
              key={o}
              on={draft.outcome === o}
              tone={o === "Up" ? "var(--color-buy)" : "var(--color-sell)"}
              onClick={() => update({ outcome: o })}
            >
              {o.toUpperCase()}
            </Choice>
          ))}
        </div>
        <Row label="Цена" problem={draft.price ? check.problems.price : undefined}>
          <input
            value={draft.price}
            onChange={(e) => update({ price: e.target.value })}
            inputMode="decimal"
            placeholder="0.47"
            className={inputClass}
          />
          <span className="num shrink-0 whitespace-nowrap">шаг {tick}</span>
        </Row>
        <Row label="Доли" problem={draft.size ? check.problems.size : undefined}>
          <input
            value={draft.size}
            onChange={(e) => update({ size: e.target.value })}
            inputMode="decimal"
            placeholder="10"
            className={inputClass}
          />
          <span className="num shrink-0 whitespace-nowrap">
            {check.notional ? `= ${check.notional} USDT` : ""}
          </span>
        </Row>
        <label className="flex items-center gap-2 text-xs text-[var(--color-muted)]">
          <input
            type="checkbox"
            checked={draft.postOnly}
            onChange={(e) => update({ postOnly: e.target.checked })}
          />
          только мейкер (post-only): ордер, пересекающий стакан, площадка отвергнет
        </label>
        <button
          type="submit"
          disabled={Boolean(reason) || !check.request || place.isPending}
          className="rounded border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
          style={{
            borderColor: draft.side === "BUY" ? "var(--color-buy)" : "var(--color-sell)",
            color: draft.side === "BUY" ? "var(--color-buy)" : "var(--color-sell)",
          }}
          title={reason ?? undefined}
        >
          {place.isPending
            ? "отправка…"
            : `${draft.side === "BUY" ? "Купить" : "Продать"} ${draft.outcome.toUpperCase()} на predict.fun`}
        </button>
        <span className="text-xs text-[var(--color-muted)]">
          {reason ??
            `лимитный ордер, потолок ${st?.max_order_usdt ?? "?"} USDT; снимается в «Ордера predict.fun» ниже`}
        </span>
        {place.error && <span className="text-xs text-[var(--color-sell)]">{place.error.message}</span>}
        {sent && (
          <span
            className={`text-xs ${
              sent.tone === "ok"
                ? "text-[var(--color-buy)]"
                : sent.tone === "muted"
                  ? "text-[var(--color-muted)]"
                  : "text-amber-300"
            }`}
          >
            {sent.text}
          </span>
        )}
      </form>
    </Panel>
  );
}

const inputClass =
  "min-w-0 flex-1 rounded border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 " +
  "text-sm outline-none placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)]";

function Choice({
  on,
  tone,
  onClick,
  big,
  children,
}: {
  on: boolean;
  tone: string;
  onClick: () => void;
  big?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded border font-medium ${big ? "px-3 py-1.5 text-sm" : "px-2 py-1 text-xs"}`}
      style={{ borderColor: on ? tone : "var(--color-border)", color: on ? tone : "var(--color-muted)" }}
    >
      {children}
    </button>
  );
}

function Row({
  label,
  problem,
  children,
}: {
  label: string;
  problem: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <label className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-x-2 gap-y-1 text-xs text-[var(--color-muted)]">
      <span>{label}</span>
      <div className="flex items-center gap-1">{children}</div>
      {problem && <span className="col-start-2 text-[var(--color-sell)]">{problem}</span>}
    </label>
  );
}
